import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import SearchParams from "simplydo/core/searchParams"
import type { OpenAPI } from "simplydo/interfaces"

import api, { websocketApi } from "api"
import ConfirmAnnouncementModal from "components/lib/ConfirmAnnouncementModal"
import { HandledIdeaPreview as IdeaPreview } from "components/lib/Ideas/IdeaPreview"
import PageLoadError from "components/lib/PageLoadError"
import { useAppSelector } from "store"
import util from "utils/utils"

import type { IProjectIdeaEvent } from "./BoardEvents"
import ProjectBoard from "./ProjectBoard"
import ProjectBoardSetup from "./ProjectBoardSetup"

export type ProjectSelectionModes = "multi"

type IProject = {
  forId: string
  canViewBoard?: boolean
  canManageBoard?: boolean
  heading?: string
  loading?: boolean
  basicLoading?: boolean
  assessmentEnabled?: boolean
  defaultLane?: OpenAPI.Schemas["ProjectLane"]
  updateDefaultLane?: (updatedLane: OpenAPI.Schemas["ProjectLane"]) => void
  // Use external ideas prop to make this component controlled
  ideas?: OpenAPI.Schemas["Idea"][]
  totalIdeas?: number
  setIdeas?: (cb: (prevIdeas: OpenAPI.Schemas["Idea"][]) => OpenAPI.Schemas["Idea"][]) => void
  fetchIdeas?: () => void

  currentIdeaId?: string
  setCurrentIdeaId?: (ideaId: string) => void

  // Multi select
  selectionMode?: ProjectSelectionModes
  selectedIdeas?: OpenAPI.Schemas["Idea"][]
  setSelectedIdeas?: Dispatch<SetStateAction<OpenAPI.Schemas["Idea"][]>>

  laneFilter?: string[]
  laneImpactMeasures?: OpenAPI.GET<"/challenges/{id}/ideas">["response"]["laneImpactMeasures"]
}

type IIdeaEvents = {
  [ideaId: string]: IProjectIdeaEvent[]
}

export type IProjectGeneralProps = {
  user: OpenAPI.GET<"/users/me">["response"]
  forId: string
  canManageBoard?: boolean
  canViewBoard?: boolean
  selectionMode?: ProjectSelectionModes
}

export type IProjectLaneProps = {
  lanes: OpenAPI.Schemas["ProjectLane"][]
  currentLane?: OpenAPI.Schemas["ProjectLane"]
  defaultLane?: OpenAPI.Schemas["ProjectLane"]
  setLanes: Dispatch<SetStateAction<OpenAPI.Schemas["ProjectLane"][]>>
  setCurrentLane: Dispatch<SetStateAction<OpenAPI.Schemas["ProjectLane"] | OpenAPI.Schemas["ProjectLane"]>>
  handleWebsocketLanes: Dispatch<SetStateAction<OpenAPI.Schemas["ProjectLane"][]>>
  updateDefaultLane?: (updatedLane: OpenAPI.Schemas["ProjectLane"]) => void
  updateProjectLaneMulti: (originalLane: string, targetLane: string) => void
}

export type IProjectIdeaProps = {
  ideas: OpenAPI.Schemas["Idea"][]
  usingPropIdeas?: boolean
  ideasTotal: number
  ideaEvents: IIdeaEvents
  ideaOpen?: boolean
  currentIdea?: OpenAPI.Schemas["Idea"]
  availableTags: OpenAPI.Schemas["Tag"][]
  setIdeaOpen: Dispatch<SetStateAction<boolean>>
  setIdeas: Dispatch<SetStateAction<OpenAPI.Schemas["Idea"][]>>
  setIdeasTotal: Dispatch<SetStateAction<number>>
  setIdeaEvents: Dispatch<SetStateAction<IIdeaEvents>>
  handleWebsocketIdeas: Dispatch<SetStateAction<OpenAPI.Schemas["Idea"][]>>
}

function Project({
  forId,
  canViewBoard,
  canManageBoard,
  heading,
  loading,
  basicLoading,
  assessmentEnabled,
  defaultLane,
  updateDefaultLane,
  laneFilter,
  laneImpactMeasures,

  // Controlled ideas
  ideas: propIdeas,
  setIdeas: propSetIdeas,
  totalIdeas,
  currentIdeaId: propCurrentIdeaId,
  setCurrentIdeaId: propSetCurrentIdeaId,
  fetchIdeas,

  // Multi select
  selectionMode,
  selectedIdeas,
  setSelectedIdeas,
}: IProject) {
  const { t } = useTranslation()

  if (propIdeas !== undefined && (propSetIdeas === undefined || totalIdeas === undefined)) {
    throw new Error(
      `If '${t("generic.ideas")}' are provided, 'set${t("common:capitalise", { key: "generic.ideas" })}'/'total${t("common:capitalise", { key: "generic.ideas" })}' must also be provided.`,
    )
  }
  if (propCurrentIdeaId !== undefined && propSetCurrentIdeaId === undefined) {
    throw new Error(
      `If 'current${t("common:capitalise", { key: "generic.idea" })}Id' is provided, 'setCurrent${t("common:capitalise", { key: "generic.idea" })}Id' must also be provided.`,
    )
  }
  const usingPropIdeas = useMemo(() => propIdeas !== undefined, [propIdeas])

  const user = useAppSelector((state) => state.user)
  const searchParams = new SearchParams(window.location.search)
  const params = useLocation().search
  const queryIdea = useMemo(() => {
    const splitParams = (params || "").split("idea=")
    if (splitParams.length) {
      return splitParams[1]
    }
    return ""
  }, [params])
  const [lanesLoading, setLanesLoading] = useState<boolean>(false)

  const [lanesLoaded, setLanesLoaded] = useState<boolean>(false)

  const [ideaOpen, setIdeaOpen] = useState<boolean>(false)
  const [ideaEvents, setIdeaEvents] = useState<IIdeaEvents>({})
  const [laneOpen, setLaneOpen] = useState<boolean>(false)
  const [currentLane, setCurrentLane] = useState<OpenAPI.Schemas["ProjectLane"] | null>(null)

  const [_lanes, setLanes] = useState<OpenAPI.Schemas["ProjectLane"][]>([])
  const lanes = useMemo(
    () => _lanes.filter((l) => !laneFilter?.length || laneFilter.indexOf(l._id) > -1),
    [_lanes, laneFilter],
  )

  const [setupModalOpen, setSetupModalOpen] = useState<boolean>(false)
  const [laneIdeaOrder, setLaneIdeaOrder] = useState<string[]>([])
  const [localIdeas, setLocalIdeas] = useState<OpenAPI.Schemas["Idea"][]>([])
  const [ideasTotal, setIdeasTotal] = useState<number>(0)
  const [rewardIdeaId, setRewardIdeaId] = useState<string>("")
  const [potentialAssigneeSearch, setPotentialAssigneeSearch] = useState<string>("")
  const [newLaneName, setNewLaneName] = useState<string>("")
  const [newLaneDescription, setNewLaneDescription] = useState<string>("")
  const [newLaneNameSaved, setNewLaneNameSaved] = useState<boolean>(true)
  const [newLaneDescriptionSaved, setNewLaneDescriptionSaved] = useState<boolean>(true)
  const [handledQueryIdea, setHandledQueryIdea] = useState<boolean>(false)
  const [confirmAnnouncementModalOpen, setConfirmAnnouncementModalOpen] = useState(false)
  const [laneAnnouncements, setLaneAnnouncements] = useState<Array<OpenAPI.Schemas["Announcement"]>>([])
  const [laneUpdateContext, setLaneUpdateContext] = useState<{
    ideaId?: string
    laneId?: string
    position?: number
    targetLaneId?: string
    originalLaneId?: string
  }>()

  // Load the idea into local state based on the current ID in the URL
  const [localCurrentIdeaId, setLocalCurrentIdeaId] = useState<string>("")
  const [_loadingCurrentIdea, setLoadingCurrentIdea] = useState<boolean>(false)
  const [currentIdea, setCurrentIdea] = useState<OpenAPI.Schemas["Idea"] | null>(null)

  const [highlightCard, setHighlightCard] = useState<string>(searchParams.has("idea") ? queryIdea : "")
  const projectWebsocket = useRef<ReturnType<(typeof websocketApi)["subscribe"]>>()
  const userId = user?._id

  const ideas = useMemo(() => propIdeas ?? localIdeas, [propIdeas, localIdeas])
  const setIdeas = useMemo(() => propSetIdeas ?? setLocalIdeas, [propSetIdeas])

  const currentIdeaId = useMemo(() => propCurrentIdeaId ?? localCurrentIdeaId, [propCurrentIdeaId, localCurrentIdeaId])
  const setCurrentIdeaId = useMemo(() => propSetCurrentIdeaId ?? setLocalCurrentIdeaId, [propSetCurrentIdeaId])

  useEffect(() => {
    if (ideas.length && queryIdea && !handledQueryIdea) {
      const ideaIds = ideas.map((i) => i._id)
      if (ideaIds.indexOf(queryIdea) > -1) {
        setHandledQueryIdea(true)
        setCurrentIdeaId(queryIdea)
      }
    }
  }, [ideas, queryIdea, handledQueryIdea, setCurrentIdeaId])

  const getCurrentIdea = useCallback(() => {
    if (currentIdeaId) {
      setCurrentIdea(null)
      setLoadingCurrentIdea(true)
      api.ideas.get(
        currentIdeaId,
        null,
        (idea) => {
          setCurrentIdea(idea)
          setLoadingCurrentIdea(false)
        },
        (err) => {
          toast.error(err.message)
          setLoadingCurrentIdea(false)
        },
      )
    }
  }, [currentIdeaId])

  useEffect(() => {
    getCurrentIdea()
  }, [getCurrentIdea])

  const ideaOrderIndex = useMemo<number>(
    () => currentIdeaId && laneIdeaOrder.findIndex((id) => id === currentIdeaId),
    [currentIdeaId, laneIdeaOrder],
  )

  const handleWebsocketData = useCallback(
    (data: {
      lanes: OpenAPI.Schemas["ProjectLane"][]
      ideas: OpenAPI.Schemas["Idea"][]
      channel: string
      fromUser: string
    }) => {
      const { lanes: updatedLanes, ideas: updatedIdeas, channel, fromUser } = data

      if (fromUser === userId) {
        return
      }

      switch (channel) {
        case "lanesUpdated":
          setLanes(updatedLanes)
          break
        case "ideasUpdated":
          setIdeas(() => updatedIdeas)
          break
        default:
          break
      }
    },
    [setIdeas, userId],
  )

  useEffect(() => setRewardIdeaId(null), [])

  const handleWebsocketLanes = useCallback(
    (updatedLanes: OpenAPI.Schemas["ProjectLane"][]) => {
      if (projectWebsocket?.current) {
        projectWebsocket.current.publish("lanesUpdated", { lanes: updatedLanes, fromUser: userId })
      }
    },
    [userId],
  )

  const handleWebsocketIdeas = useCallback(
    (updatedIdeas: OpenAPI.Schemas["Idea"][]) => {
      if (projectWebsocket?.current) {
        projectWebsocket.current.publish("ideasUpdated", { ideas: updatedIdeas, fromUser: userId })
      }
    },
    [userId],
  )

  const moveIdea = useCallback(
    (amount: number) => {
      if (ideaOrderIndex === -1) return
      const newIndex = ideaOrderIndex + amount
      const newIdeaId = laneIdeaOrder[newIndex]
      if (newIdeaId) {
        setCurrentIdeaId(newIdeaId)
      }
    },
    [ideaOrderIndex, laneIdeaOrder, setCurrentIdeaId],
  )

  useEffect(() => {
    if (!forId) return
    // @ts-ignore
    projectWebsocket.current = websocketApi.subscribe(`projectBoard-${forId}`, (data) => handleWebsocketData(data))
    return () => projectWebsocket.current.unsubscribe()
  }, [handleWebsocketData, forId])

  const onUpdateIdea = useCallback(
    (data: Partial<OpenAPI.Schemas["Idea"]>) => {
      if (!currentIdeaId) {
        return
      }
      setIdeas((prevIdeas) =>
        prevIdeas.map((i) => {
          if (i._id !== currentIdeaId) {
            return i
          }
          return { ...i, ...data }
        }),
      )
      if (data.impacts) {
        fetchIdeas()
      }
    },
    [currentIdeaId, setIdeas, fetchIdeas],
  )

  const getBoardLanes = useCallback(() => {
    setLanesLoading(true)
    api.boards.getProjectLanes(
      forId,
      ({ lanes: newLanes }) => {
        setLanes(newLanes)
        setLanesLoading(false)
        setLanesLoaded((prevLoaded) => prevLoaded || true)
      },
      () => {
        setLanesLoading(false)
      },
    )
  }, [forId])

  useEffect(() => {
    getBoardLanes()
  }, [getBoardLanes])

  useEffect(() => {
    if (currentLane && currentLane) {
      const updatedLane = lanes.find((l) => l._id === (currentLane?._id ? currentLane?._id : currentLane?.id))
      if (updatedLane) setCurrentLane(updatedLane)
    }
  }, [currentLane, lanes])

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!currentIdeaId) return
    api.boards.getProjectIdeaEvents(
      forId,
      currentIdeaId,
      (data) => {
        const events = { ...ideaEvents }
        if (!events[currentIdeaId]) events[currentIdeaId] = []
        events[currentIdeaId] = data.events
        setIdeaEvents(events)
      },
      () => {},
    )
  }, [forId, currentIdeaId])

  const cardClicked = (ideaId: string) => {
    setIdeaOpen(true)
    setCurrentIdeaId(ideaId)
    if (ideaId === highlightCard) {
      setHighlightCard("")
    }
  }

  const updateProjectLane = useCallback(
    (ideaId: string, targetLaneId: string, position: number, announcementIds: Array<string>) => {
      setRewardIdeaId(null)
      const idea = ideas.filter((i) => i._id === ideaId)[0]
      const board = idea?.projectManagement?.boards?.find((b) => b.forId === idea.challenge)
      const originalLane = board?.lane
      if (
        !canManageBoard &&
        !(board?.assignees?.indexOf(user._id) > -1) &&
        !util.hasPermission(user, "challenge.editProjectBoard", forId)
      ) {
        return false
      }
      // If no new lane or order, then return:
      if (!((board?.lane || "default") !== targetLaneId || position !== board?.order || 0)) return
      api.boards.updateProjectIdea(
        forId,
        ideaId,
        targetLaneId,
        { order: position, sendAnnouncements: announcementIds },
        ({ idea: updatedIdea, event, updatedIdeas }) => {
          if (event) {
            const newIdeaEvents = { ...ideaEvents }
            newIdeaEvents[updatedIdea._id] = Object.assign([], newIdeaEvents[updatedIdea._id])
            newIdeaEvents[updatedIdea._id].splice(0, 0, event)
            setIdeaEvents(newIdeaEvents)
          }

          const moveToLane = lanes.find((l) => l._id === targetLaneId)
          if (originalLane !== targetLaneId && moveToLane?.metaType === "completed") {
            setRewardIdeaId(ideaId)
          }
          let newIdeas = null
          setIdeas((prev) => {
            newIdeas = prev.map((i) => {
              const thisIdeaBoard = i?.projectManagement?.boards?.find((b) => b.forId === i.challenge)

              // Update this idea if it's the one that was moved:
              if (i._id === ideaId) {
                if (thisIdeaBoard) {
                  // Update only specific fields so we don't lose the assigneeUsers array:
                  const newBoards = updatedIdea.projectManagement?.boards
                  const newBoard = newBoards.find((b) => b.forId === forId)
                  thisIdeaBoard.lane = newBoard?.lane
                  thisIdeaBoard.updatedAt = newBoard?.updatedAt
                  thisIdeaBoard.order = newBoard?.order
                }
              } else {
                // Otherwise check if this idea is in the updatedIdeas from the server in case we need to update an order
                const currentUpdatedIdea = updatedIdeas.find((c) => c._id === i._id)
                if (currentUpdatedIdea) {
                  const currentUpdatedIdeaBoard = currentUpdatedIdea?.projectManagement?.boards?.find(
                    (b) => b.forId === forId,
                  )
                  thisIdeaBoard.order = currentUpdatedIdeaBoard.order
                }
              }
              return i
            })
            return newIdeas
          })
          handleWebsocketIdeas(newIdeas)
          fetchIdeas()
        },
        () => {},
      )
      return true
    },
    [user, ideas, canManageBoard, forId, lanes, setIdeas, handleWebsocketIdeas, ideaEvents, fetchIdeas],
  )

  const updateProjectLaneMulti = useCallback(
    (originalLane: string, targetLane: string, announcementIds: Array<string>) => {
      api.boards.updateProjectIdeasMulti(
        forId,
        { originalLane, targetLane, sendAnnouncements: announcementIds },
        ({ updatedIdeas, events }) => {
          if (events) {
            const newIdeaEvents = { ...events, ...ideaEvents }
            setIdeaEvents(newIdeaEvents)
          }
          let newIdeas = null
          setIdeas((prevIdeas) => {
            newIdeas = prevIdeas.map((i) => {
              const thisIdeaBoard = i?.projectManagement?.boards?.find((b) => b.forId === i.challenge)
              // Update this idea if it's the one that was moved:
              if (thisIdeaBoard) {
                const updatedIdea = updatedIdeas.find((updIdea) => i._id === updIdea._id)
                if (updatedIdea) {
                  // Update only specific fields so we don't lose the assigneeUsers array:
                  const newBoards = updatedIdea.projectManagement?.boards
                  const newBoard = newBoards.find((b) => b.forId === forId)
                  thisIdeaBoard.lane = newBoard?.lane
                  thisIdeaBoard.updatedAt = newBoard?.updatedAt
                  thisIdeaBoard.order = newBoard?.order
                }
              }
              return i
            })
            return newIdeas
          })
          handleWebsocketIdeas(newIdeas)
          fetchIdeas()
          toast.success(`${t("common:capitalise", { key: "generic.ideas" })} moved`)
        },
        () => {
          toast.error(`Unable to move ${t("generic.ideas")}`)
        },
      )
    },
    [forId, setIdeas, handleWebsocketIdeas, ideaEvents, t, fetchIdeas],
  )

  const onIdeaActivity = useCallback(
    (ideaId: string) => {
      const idea = ideas.find((i) => i._id === ideaId)
      const board = idea?.projectManagement?.boards?.find((b) => b.forId === idea.challenge)
      if (
        (board.assignees && board.assignees.indexOf(userId) > -1) ||
        canManageBoard ||
        canViewBoard ||
        util.hasPermission(user, "challenge.viewProjectActivity", forId)
      ) {
        let updatedIdeas = null
        setIdeas((prevIdeas) => {
          updatedIdeas = prevIdeas.map((i) => {
            if (i._id !== ideaId) return i
            const updatedBoards = i.projectManagement.boards.map((b) => {
              if (b.forId !== forId) return b
              return { ...b, updatedAt: new Date().toString() }
            })
            return { ...i, projectManagement: { boards: updatedBoards } }
          })
          return updatedIdeas
        })
        handleWebsocketIdeas(updatedIdeas)
      }
    },
    [ideas, userId, user, canManageBoard, canViewBoard, setIdeas, handleWebsocketIdeas, forId],
  )

  const checkUpdateProjectLane = useCallback(
    (ideaId, laneId, position = 0) => {
      const currentIdea = ideas.find((i) => i._id === ideaId)
      const newLane = lanes.find((l) => l._id === laneId)

      // Do nothing if the idea is already in the lane and position
      if (
        currentIdea?.projectManagement?.boards.find((b) => b.forId === forId && b.order === position)?.lane === laneId
      ) {
        return
      }

      if (!newLane?.announcements) {
        updateProjectLane(ideaId, laneId, position, [])
      } else {
        setLaneAnnouncements(newLane.announcements)
        setConfirmAnnouncementModalOpen(true)
        setLaneUpdateContext({ laneId, ideaId, position })
      }
    },
    [updateProjectLane, forId, lanes, ideas],
  )

  const checkUpdateProjectLaneMulti = useCallback(
    (originalLaneId: string, targetLaneId: string) => {
      const newLane = lanes.find((l) => l._id === targetLaneId)
      if (!newLane?.announcements) {
        updateProjectLaneMulti(originalLaneId, targetLaneId, [])
      } else {
        setLaneAnnouncements(newLane.announcements)
        setConfirmAnnouncementModalOpen(true)
        setLaneUpdateContext({ originalLaneId, targetLaneId })
      }
    },
    [updateProjectLaneMulti, lanes],
  )

  if (!user) return <PageLoadError title={"Please log in to view the project board"} message={undefined} />
  if (!canViewBoard && !util.hasPermission(user, "challenge.viewProjectBoard", forId)) return null

  const generalProps: IProjectGeneralProps = {
    user,
    forId,
    canManageBoard,
    canViewBoard,
    selectionMode,
  }

  const laneProps: IProjectLaneProps = {
    lanes,
    setLanes,
    handleWebsocketLanes,
    currentLane,
    setCurrentLane,
    defaultLane,
    updateDefaultLane,
    updateProjectLaneMulti,
  }

  const ideaProps: IProjectIdeaProps = {
    ideas,
    usingPropIdeas,
    ideasTotal,
    setIdeas,
    setIdeasTotal,
    ideaEvents,
    setIdeaEvents,
    handleWebsocketIdeas,
    ideaOpen,
    setIdeaOpen,
    currentIdea,
  }

  return (
    <>
      <ConfirmAnnouncementModal
        announcements={laneAnnouncements}
        modalOpen={confirmAnnouncementModalOpen}
        handleConfirm={(announcementIds) => {
          setConfirmAnnouncementModalOpen(false)
          if (laneUpdateContext.ideaId) {
            updateProjectLane(
              laneUpdateContext.ideaId,
              laneUpdateContext.laneId,
              laneUpdateContext.position,
              announcementIds,
            )
          } else {
            updateProjectLaneMulti(laneUpdateContext.originalLaneId, laneUpdateContext.targetLaneId, announcementIds)
          }
          setLaneUpdateContext({})
        }}
      />
      {!usingPropIdeas ? (
        <IdeaPreview
          isProjectBoard
          ideaId={currentIdeaId}
          closeModal={() => setCurrentIdeaId(null)}
          moveIdea={moveIdea}
          canGoNextIdea={ideaOrderIndex + 1 !== ideas.length}
          canGoPrevIdea={ideaOrderIndex !== 0}
          onUpdateIdea={(_ideaId, updateData) => onUpdateIdea(updateData)}
          updateProjectLane={checkUpdateProjectLane}
          onIdeaActivity={onIdeaActivity}
          assessmentEnabled={assessmentEnabled}
          lanes={lanes}
        />
      ) : null}

      <ProjectBoardSetup
        forId={forId}
        dataLoaded={lanesLoaded}
        lanes={lanes}
        setLanes={setLanes}
        setupModalOpen={setupModalOpen}
        setSetupModalOpen={setSetupModalOpen}
        laneFilter={laneFilter}
        canManageBoard={canManageBoard}
      />
      <ProjectBoard
        {...generalProps}
        {...ideaProps}
        {...laneProps}
        canManageBoard={canManageBoard}
        cardClicked={cardClicked}
        heading={heading}
        initialDataLoaded={lanesLoaded}
        basicLoading={basicLoading}
        loading={loading || lanesLoading}
        setLaneIdeaOrder={setLaneIdeaOrder}
        setLaneOpen={setLaneOpen}
        setNewLaneDescription={setNewLaneDescription}
        setNewLaneName={setNewLaneName}
        setCurrentIdeaId={setCurrentIdeaId}
        updateProjectLane={checkUpdateProjectLane}
        onIdeaActivity={onIdeaActivity}
        setRewardIdeaId={setRewardIdeaId}
        rewardIdeaId={rewardIdeaId}
        setSetupModalOpen={setSetupModalOpen}
        highlightCard={highlightCard}
        updateProjectLaneMulti={checkUpdateProjectLaneMulti}
        lanes={lanes}
        laneFilter={laneFilter}
        laneOpen={laneOpen}
        laneImpactMeasures={laneImpactMeasures}
        newLaneDescription={newLaneDescription}
        newLaneDescriptionSaved={newLaneDescriptionSaved}
        newLaneName={newLaneName}
        newLaneNameSaved={newLaneNameSaved}
        setNewLaneDescriptionSaved={setNewLaneDescriptionSaved}
        setNewLaneNameSaved={setNewLaneNameSaved}
        selectedIdeas={selectedIdeas}
        setSelectedIdeas={setSelectedIdeas}
      />
    </>
  )
}

export default Project
